<template>
    <Dialog v-model:visible="dialogVisible" :closable="false" class="h-full" content-class="h-full" style="width: 90vw" :header="'Expression Builder'" :modal="true" :close-on-click-modal="false">
        <NestedExpressionBuilder v-model="expr" :allow-delete="false" :elementType="resolvedElementType" :defaultTable="defaultTable"/>
        <template #footer>
            <UnsavedChanges :value="copied" @save="save" @close="close"/>
        </template>
    </Dialog>
</template>
<script setup lang="ts">
import NestedExpressionBuilder from "./NestedExpressionBuilder.vue";
import {computed, inject, markRaw, nextTick, ref, watch} from "vue";
import {getProperty, isLink} from "@/lib/util/ModelUtil";
import {useStore} from "vuex";
import _ from "lodash-es";

const props = defineProps({
    modelValue: {
        type: Object,
        default: null
    },
    visible: {
        type: Boolean,
        default: undefined
    },
    elementType: {
        type: String,
        default: ''
    },
    defaultTable: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['update:modelValue', 'save', 'close', 'update:visible'])

const store = useStore()

const internalVisible = ref(true)
const isControlled = computed(() => props.modelValue !== null)

const dialogVisible = computed({
    get: () => typeof props.visible === 'boolean' ? props.visible : internalVisible.value,
    set: (value) => {
        internalVisible.value = value
        emit('update:visible', value)
    }
})

const element = inject('element', null)
const item = ref(element ? markRaw(element) : null)

const modeler = computed(() => store.getters["graphStore/getModeler"])

const resolvedElementType = computed(() => {
    if (props.elementType) {
        return props.elementType
    }
    return item.value?.type
})

const copied = ref(_.cloneDeep(isControlled.value ? props.modelValue : getProperty(item.value, 'nestedExpression')))

watch(() => props.modelValue, (value) => {
    if (!isControlled.value) {
        return
    }
    copied.value = _.cloneDeep(value)
}, {deep: true})

const expr = computed({
    get: () => {
        return copied.value
    },
    set: val => {
        copied.value = null
        nextTick(() => {
            copied.value = val
        })
    }
})

const save = () => {
    if (isControlled.value) {
        emit('update:modelValue', _.cloneDeep(copied.value))
        emit('save', _.cloneDeep(copied.value))
        close()
        return
    }

    modeler.value.get('modeling').updateProperties(item.value, {
        nestedExpression: copied.value
    })
    if (isLink(item.value)) {
        modeler.value.get('modeling').updateLinkLabel(item.value, copied.value)
    } else {
        // just a fake update to trigger the update of attribute checks label
        modeler.value.get('modeling').updateLabel(item.value, item.value.businessObject.name)
    }
    close()
}


const close = () => {
    dialogVisible.value = false
    emit('close')
}

const editMode = computed(() => {
    return store.getters["graphStore/getEditMode"]
})

watch(editMode, (newValue) => {
    if (isControlled.value) {
        return
    }
    if (!newValue) {
        close()
    }
})

</script>
